import {
  useMemo,
  ReactNode,
  cloneElement,
  Children,
  ReactElement,
  useEffect,
  useCallback,
  useState,
  useRef,
} from 'react';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';
import locale from '@glu/locale';
import classnames from 'classnames';
import useStyles from './Popover.styles';

const noop = () => null;
const emptyObj = {};

const containerId = 'popovers-default-container';

const defaultGetPopoverContainer = () => {
  const existingContainer = document.getElementById(containerId);

  if (existingContainer) {
    return existingContainer;
  }

  const container = document.createElement('div');
  container.setAttribute('id', containerId);

  document.body.appendChild(container);

  return container;
};

type TUsePopperArgs = Parameters<typeof usePopper>;
type TUsePopperOptions = NonNullable<TUsePopperArgs[2]>;

export interface IPopoverProps {
  /** Children to be wrapped and respond to events in order to show the popover */
  children: ReactNode | (() => JSX.Element);
  /** CSS ClassName to use in addition to built in classes */
  className?: string;
  /** Contents of the popover */
  content?:
    | ReactNode
    | (({
        closePopover,
        visible,
      }: {
        closePopover(): void;
        visible: boolean;
      }) => JSX.Element | JSX.Element[]);
  /** True if the popover is open by default and thus should be showing, false if it should not */
  defaultVisible?: boolean;
  /**
   * If true the built in a11y button won't be shown.
   * You are expected to provide your own in this case
   */
  disableA11yButton?: boolean;
  /** Getter for the element to render popover to */
  getPopoverContainer?: typeof defaultGetPopoverContainer;
  /**
   * Function that is called whenever the open state of the popover should change.
   * This allows coordinated external control via the open prop. The provided function
   * will receive a single boolean argument to indicate the new open state.
   */
  onOpenStateChange?(newState: boolean): void;
  /** Allow clicks inside the popover with closing */
  enablePopoverClick?: boolean;
  /** If true the the outside click is disabled in order to hide the popover */
  disableHideOnOutsideClick?: boolean;
  /** If popover is currently in an open state */
  open?: boolean;
  /** If true the popover will open on hover in addition to opening on click */
  openOnHover?: boolean;
  /** True if you wish to disable the arrow connecting popover to its reference element */
  noArrow?: boolean;
  /** Number of milliseconds to wait before displaying tooltip on hover */
  openOnHoverDelay?: number;
  /** Any value accepted by popper to tell it where to position to popover */
  placement?: TUsePopperOptions['placement'];
  /** Custom options to pass directly to popperJs.
   *  Using these could break your code if Popover ever switches to something else */
  popperOptions?: TUsePopperOptions;
  /** Title of the popover */
  title?: ReactNode | (() => JSX.Element);
  /** Manually set zIndex for popover to use  */
  zIndex?: number;
}

const modifiers = [
  {
    name: 'offset',
    options: {
      offset: [0, 12],
    },
  },
  {
    name: 'preventOverflow',
    options: {
      altAxis: true,
      padding: 16,
    },
  },
  {
    name: 'flip',
    options: {
      padding: 16,
    },
  },
];
const tooltipVisibilityAttr = 'data-show';

const Popover = ({
  children,
  className,
  content,
  defaultVisible = false,
  disableA11yButton = false,
  disableHideOnOutsideClick = false,
  enablePopoverClick = false,
  getPopoverContainer = defaultGetPopoverContainer,
  noArrow = false,
  onOpenStateChange = noop,
  open,
  openOnHover = false,
  openOnHoverDelay,
  placement = 'bottom',
  popperOptions = emptyObj,
  title,
  zIndex = 1000,
}: IPopoverProps) => {
  const classes = useStyles(useMemo(() => ({ zIndex }), [zIndex]));
  const externalControl = open !== undefined;
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const timeoutRef = useRef<number>();
  const { attributes, styles, update } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: noArrow
      ? modifiers
      : [{ name: 'arrow', options: { element: arrowElement } }, ...modifiers],
    ...popperOptions,
  });
  const visible = popperElement?.hasAttribute(tooltipVisibilityAttr) ?? false;

  const hide = useCallback(async () => {
    clearTimeout(timeoutRef.current);
    popperElement?.removeAttribute(tooltipVisibilityAttr);
    onOpenStateChange(false);
    await update?.();
  }, [onOpenStateChange, popperElement, update]);

  const show = useCallback(async () => {
    popperElement?.setAttribute(tooltipVisibilityAttr, '');
    onOpenStateChange(true);
    await update?.();
    popperElement?.querySelector('input')?.focus();
    popperElement?.querySelector('button')?.focus();
  }, [onOpenStateChange, popperElement, update]);

  const toggle = useCallback(() => {
    if (visible) {
      hide();
    } else {
      show();
    }
  }, [hide, visible, show]);

  const onHover = useCallback(() => {
    if (!openOnHover) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(show, openOnHoverDelay);
  }, [openOnHoverDelay, openOnHover, show]);

  const onHoverOut = useCallback(() => {
    if (!openOnHover) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(hide, openOnHoverDelay);
  }, [openOnHoverDelay, hide, openOnHover]);

  // Global event listeners for hiding and showing the popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const element = event.target;
      if (
        element instanceof Node &&
        (referenceElement?.contains(element) ||
          (enablePopoverClick && popperElement?.contains(element)))
      ) {
        return;
      }

      hide();
    };

    const handleKeyDown = ({ code }: KeyboardEvent) => {
      // esc or tab out hides tooltip
      if (code === 'Escape' || (code === 'Tab' && !enablePopoverClick)) {
        hide();
      }
    };

    if (!disableHideOnOutsideClick && !externalControl) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    disableHideOnOutsideClick,
    enablePopoverClick,
    externalControl,
    hide,
    popperElement,
    referenceElement,
  ]);

  useEffect(() => {
    if (!externalControl) return;
    if (open) {
      show();
    } else {
      hide();
    }
  }, [externalControl, hide, open, show]);

  // clear the timeout when this unmounts
  useEffect(() => () => clearTimeout(timeoutRef.current), []);
  const containerEl = getPopoverContainer();

  const events = useMemo(() => {
    if (externalControl) return undefined;
    return {
      onClick: toggle,
      onMouseOver: onHover,
      onMouseLeave: onHoverOut,
    };
  }, [externalControl, onHover, onHoverOut, toggle]);

  const child = useMemo(() => {
    if (!children) return null;
    if (typeof children === 'function') {
      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <span ref={setReferenceElement} {...events}>
          {children()}
        </span>
      );
    }
    return cloneElement(Children.only(children as ReactElement), {
      ref: setReferenceElement,
      ...events,
    });
  }, [children, events, setReferenceElement]);

  useEffect(() => {
    if (defaultVisible) {
      show();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <>
      {child}
      {!disableA11yButton && (
        <button className={classes.a11ybutton} data-qa="a11ybutton" onClick={show} type="button">
          {locale.get('showPopover')}
        </button>
      )}
      {containerEl &&
        ReactDOM.createPortal(
          <div
            ref={setPopperElement}
            className={classnames(classes.root, className)}
            style={styles.popper}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attributes.popper}
            onMouseEnter={events?.onMouseOver}
            onMouseLeave={events?.onMouseLeave}
          >
            {title ? (
              <div className={classes.title}>{typeof title === 'function' ? title() : title}</div>
            ) : null}
            {content && typeof content === 'function'
              ? content({ closePopover: hide, visible })
              : content}
            {/* data-popper-arrow is used by the popper library */}
            {!noArrow && (
              <div
                ref={setArrowElement}
                className={classes.arrow}
                data-popper-arrow=""
                style={styles.arrow}
              />
            )}
          </div>,
          containerEl
        )}
    </>
  );
};

export default Popover;
